import React, { useEffect, useState, useRef, useCallback } from "react";
import { FlatList, Pressable, KeyboardAvoidingView, Platform, ActivityIndicator, View, Text } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import styled from "styled-components/native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { collection, query, orderBy, onSnapshot, getCountFromServer, where, limit, startAfter, getDocs, QueryDocumentSnapshot } from "firebase/firestore";
import { PeopleIcon, SendIcon } from "@/components/Icons";
import { db } from "@/lib/firebase";
import { sendMessage } from "@/lib/services";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useDev } from "@/contexts/DevContext";
import { colors, radius, font, spacing } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import type { Message } from "@/lib/types";
import type { Timestamp } from "firebase/firestore";

/* ─── types ─── */

interface MessageDoc extends Message {
  id: string;
}

/* ─── styled components ─── */

const Screen = styled.View`
  flex: 1;
  background-color: ${colors.bg};
`;

const HeaderBar = styled.View`
  background-color: ${colors.white};
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
  padding-horizontal: ${spacing.xl}px;
  padding-vertical: ${spacing.md}px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.View`
  flex: 1;
`;

const HeaderName = styled.Text`
  font-size: 16px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const HeaderSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const MembersBtn = styled.View<{ pressed?: boolean }>`
  flex-direction: row;
  align-items: center;
  background-color: ${colors.brand50};
  border-radius: ${radius.md}px;
  padding-horizontal: 12px;
  padding-vertical: 8px;
  opacity: ${(p) => (p.pressed ? 0.7 : 1)};
`;

const MembersBtnText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.brand};
  margin-left: 4px;
`;

const MessagesContainer = styled.View`
  flex: 1;
  padding-horizontal: ${spacing.lg}px;
`;

const BubbleRow = styled.View<{ isOwn: boolean }>`
  flex-direction: row;
  justify-content: ${(p) => (p.isOwn ? "flex-end" : "flex-start")};
  margin-vertical: 3px;
`;

const Avatar = styled.View`
  width: 32px;
  height: 32px;
  border-radius: ${radius.full}px;
  background-color: ${colors.brandLight};
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  margin-top: 4px;
`;

const AvatarLetter = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.brandDark};
`;

const Bubble = styled.View<{ isOwn: boolean }>`
  max-width: 75%;
  background-color: ${(p) => (p.isOwn ? colors.brand : colors.white)};
  border-radius: ${radius.lg}px;
  border-top-left-radius: ${(p) => (p.isOwn ? radius.lg : 4)}px;
  border-top-right-radius: ${(p) => (p.isOwn ? 4 : radius.lg)}px;
  padding-horizontal: 14px;
  padding-vertical: 10px;
  border-width: ${(p) => (p.isOwn ? 0 : 1)}px;
  border-color: ${colors.line};
`;

const AuthorName = styled.Text<{ isOwn: boolean }>`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${(p) => (p.isOwn ? "rgba(255,255,255,0.8)" : colors.brand)};
  margin-bottom: 2px;
`;

const BubbleText = styled.Text<{ isOwn: boolean }>`
  font-size: 14px;
  font-weight: ${font.regular};
  color: ${(p) => (p.isOwn ? colors.white : colors.ink)};
  line-height: 20px;
`;

const BubbleTime = styled.Text<{ isOwn: boolean }>`
  font-size: 10px;
  color: ${(p) => (p.isOwn ? "rgba(255,255,255,0.65)" : colors.inkMuted)};
  margin-top: 4px;
  text-align: right;
`;

const InputBar = styled.View`
  flex-direction: row;
  align-items: flex-end;
  padding-horizontal: ${spacing.lg}px;
  padding-vertical: ${spacing.sm}px;
  background-color: ${colors.white};
  border-top-width: 1px;
  border-top-color: ${colors.line};
`;

const Input = styled.TextInput`
  flex: 1;
  min-height: 40px;
  max-height: 100px;
  background-color: ${colors.bg};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding-horizontal: 14px;
  padding-vertical: 10px;
  font-size: 14px;
  color: ${colors.ink};
`;

const SendBtn = styled.View<{ disabled?: boolean; pressed?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${radius.full}px;
  background-color: ${(p) => (p.disabled ? colors.line : colors.brand)};
  align-items: center;
  justify-content: center;
  margin-left: ${spacing.sm}px;
  opacity: ${(p) => (p.pressed ? 0.7 : 1)};
`;

const LoadingContainer = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const EmptyContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

/* ─── helpers ─── */

function formatTime(ts: Timestamp | null | undefined): string {
  if (!ts) return "";
  const d = ts.toDate();
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/* ─── component ─── */

export default function ChatScreen() {
  const { id, name } = useLocalSearchParams<{ id: string; name: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const { club } = useClub();
  const { impersonating } = useDev();
  const insets = useSafeAreaInsets();

  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [olderMessages, setOlderMessages] = useState<MessageDoc[]>([]);
  const [memberCount, setMemberCount] = useState<number>(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [loading, setLoading] = useState(false); // Firestore cache serves data fast
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const flatListRef = useRef<FlatList>(null);
  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);
  const PAGE_SIZE = 50;

  const clubId = club?.id;
  const channelId = id;

  // Resolve effective user (impersonation-aware)
  const effectiveUserId = impersonating?.userId ?? user?.uid ?? "";
  const effectiveUserName =
    impersonating?.displayName ??
    user?.displayName ??
    user?.email?.split("@")[0] ??
    "";

  // Real-time listener for latest messages (paginated)
  useEffect(() => {
    if (!clubId || !channelId) return;

    const messagesRef = collection(db, "clubs", clubId, "channels", channelId, "messages");
    const q = query(messagesRef, orderBy("createdAt", "desc"), limit(PAGE_SIZE));

    setOlderMessages([]);
    setHasMore(true);
    lastDocRef.current = null;

    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as MessageDoc[];
      setMessages(docs);
      if (snap.docs.length > 0) {
        lastDocRef.current = snap.docs[snap.docs.length - 1];
      }
      if (snap.docs.length < PAGE_SIZE) setHasMore(false);
      setLoading(false);
    });

    return unsub;
  }, [clubId, channelId]);

  const loadOlderMessages = async () => {
    if (!clubId || !channelId || loadingMore || !hasMore || !lastDocRef.current) return;
    setLoadingMore(true);
    try {
      const messagesRef = collection(db, "clubs", clubId, "channels", channelId, "messages");
      const q = query(messagesRef, orderBy("createdAt", "desc"), startAfter(lastDocRef.current), limit(PAGE_SIZE));
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as MessageDoc[];
      if (docs.length === 0) { setHasMore(false); return; }
      lastDocRef.current = snap.docs[snap.docs.length - 1];
      setOlderMessages((prev) => [...prev, ...docs]);
      if (snap.docs.length < PAGE_SIZE) setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // Combined messages = live page + older static pages
  const allMessages = [...messages, ...olderMessages];

  // Fetch member count
  useEffect(() => {
    if (!clubId || !channelId) return;

    const membersRef = collection(
      db,
      "clubs",
      clubId,
      "channels",
      channelId,
      "members"
    );

    getCountFromServer(membersRef).then((snap) => {
      setMemberCount(snap.data().count);
    });

    // Realtime pending count for the dot
    const pendingQuery = query(membersRef, where("status", "==", "pending"));
    const unsubPending = onSnapshot(pendingQuery, (snap) => {
      setPendingCount(snap.size);
    });

    // Mark this channel as read for the current user
    if (effectiveUserId) {
      import("firebase/firestore").then(({ doc, setDoc, Timestamp }) => {
        setDoc(
          doc(db, "clubs", clubId, "channels", channelId, "members", effectiveUserId),
          { lastReadAt: Timestamp.now() },
          { merge: true }
        ).catch(() => {});
      });
    }

    return unsubPending;
  }, [clubId, channelId, effectiveUserId]);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || !clubId || !channelId || sending) return;

    setSending(true);
    setText("");
    try {
      await sendMessage(clubId, channelId, {
        text: trimmed,
        authorId: effectiveUserId,
        authorName: effectiveUserName,
      });
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSending(false);
    }
  }, [text, clubId, channelId, sending, effectiveUserId, effectiveUserName]);

  const renderMessage = useCallback(
    ({ item }: { item: MessageDoc }) => {
      const isOwn = item.authorId === effectiveUserId;

      if (item.type === "system") {
        return (
          <EmptyContainer style={{ padding: 8 }}>
            <EmptyText>{item.text}</EmptyText>
          </EmptyContainer>
        );
      }

      return (
        <BubbleRow isOwn={isOwn}>
          {!isOwn && (
            <Avatar>
              <AvatarLetter>
                {(item.authorName || "?")[0].toUpperCase()}
              </AvatarLetter>
            </Avatar>
          )}
          <Bubble isOwn={isOwn}>
            <AuthorName isOwn={isOwn}>
              {isOwn ? "You" : item.authorName}
            </AuthorName>
            <BubbleText isOwn={isOwn}>{item.text}</BubbleText>
            <BubbleTime isOwn={isOwn}>{formatTime(item.createdAt)}</BubbleTime>
          </Bubble>
        </BubbleRow>
      );
    },
    [effectiveUserId]
  );

  const keyExtractor = useCallback((item: MessageDoc) => item.id, []);

  return (
    <Screen>
      <ContentWidth style={{ flex: 1 }}>
        {/* Header */}
        <HeaderBar>
          <HeaderLeft>
            <HeaderName>{name || "Chat"}</HeaderName>
            <HeaderSub>
              {memberCount} {memberCount === 1 ? "member" : "members"}
            </HeaderSub>
          </HeaderLeft>
          <Pressable
            onPress={() =>
              router.push(`/(app)/channel-members?id=${channelId}`)
            }
          >
            {({ pressed }: { pressed: boolean }) => (
              <MembersBtn pressed={pressed}>
                <PeopleIcon size={16} color={colors.brand} />
                <MembersBtnText>Members</MembersBtnText>
                {pendingCount > 0 && (
                  <View style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    backgroundColor: colors.danger,
                    alignItems: "center",
                    justifyContent: "center",
                    paddingHorizontal: 4,
                    borderWidth: 2,
                    borderColor: colors.white,
                  }}>
                    <Text style={{ color: "#fff", fontSize: 9, fontWeight: "800" }}>{pendingCount}</Text>
                  </View>
                )}
              </MembersBtn>
            )}
          </Pressable>
        </HeaderBar>

        {/* Messages */}
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          keyboardVerticalOffset={Platform.OS === "ios" ? 90 : 0}
        >
          {loading ? (
            <LoadingContainer>
              <ActivityIndicator size="large" color={colors.brand} />
            </LoadingContainer>
          ) : (
            <MessagesContainer>
              <FlatList
                ref={flatListRef}
                data={allMessages}
                renderItem={renderMessage}
                keyExtractor={keyExtractor}
                inverted
                contentContainerStyle={{ paddingVertical: 12 }}
                showsVerticalScrollIndicator={false}
                onEndReached={loadOlderMessages}
                onEndReachedThreshold={0.3}
                ListFooterComponent={
                  loadingMore ? <View style={{ padding: 16, alignItems: "center" }}><ActivityIndicator color={colors.brand} /></View> : null
                }
                ListEmptyComponent={
                  <EmptyContainer>
                    <EmptyText>
                      No messages yet. Say hello!
                    </EmptyText>
                  </EmptyContainer>
                }
                initialNumToRender={20}
                maxToRenderPerBatch={15}
                windowSize={7}
              />
            </MessagesContainer>
          )}

          {/* Input Bar */}
          <InputBar style={{ paddingBottom: Math.max(insets.bottom, 8) }}>
            <Input
              placeholder="Type a message..."
              placeholderTextColor={colors.inkMuted}
              value={text}
              onChangeText={setText}
              multiline
              onSubmitEditing={handleSend}
              blurOnSubmit={false}
            />
            <Pressable onPress={handleSend} disabled={!text.trim() || sending}>
              {({ pressed }: { pressed: boolean }) => (
                <SendBtn disabled={!text.trim() || sending} pressed={pressed}>
                  <SendIcon size={18} color={colors.white} />
                </SendBtn>
              )}
            </Pressable>
          </InputBar>
        </KeyboardAvoidingView>
      </ContentWidth>
    </Screen>
  );
}
